/**
 * 챗봇 대화창 — FAB 위로 펼쳐지는 popup.
 *
 * 구성:
 *  - 헤더: AI 비서 + 사용량 + 메뉴 + 최소화 + 닫기(영구 숨김)
 *  - 메시지 영역: 무한 스크롤 + Markdown 렌더링
 *  - 입력창: ChatbotInput
 *
 * 무한 스크롤:
 *  - 메시지 영역 상단에 sentinel 배치
 *  - IntersectionObserver 로 감지 → loadMore()
 *  - 옛 메시지 prepend 시 스크롤 위치 보존
 *
 * 자동 스크롤:
 *  - 새 메시지 도착 시 사용자가 이미 가장 아래에 있으면 스크롤 다운
 *  - 옛 대화 읽고 있을 때는 자동 스크롤 안 함
 */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MinimizeRoundedIcon from "@mui/icons-material/MinimizeRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { ds } from "../../styles/designSystem";
import { useChatbot } from "./ChatbotProvider";
import ChatbotInput from "./ChatbotInput";
import ChatbotMessageBubble from "./ChatbotMessageBubble";
import ChatbotMenu from "./ChatbotMenu";

export default function ChatbotWindow() {
    const {
        visible, open, compact,
        messages, hasMore, usage, sending, streamingText,
        toggleOpen, toggleCompact, hide, sendMessage, loadMore, resetSession,
    } = useChatbot();

    const listRef = useRef(null);
    const topSentinelRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const wasAtBottomRef = useRef(true);
    const prevScrollHeightRef = useRef(0);

    // ─── 무한 스크롤 IntersectionObserver ──────────────────
    useEffect(() => {
        if (!open) return;
        const sentinel = topSentinelRef.current;
        const root = listRef.current;
        if (!sentinel || !root || !hasMore) return;

        const io = new IntersectionObserver(async (entries) => {
            if (entries[0].isIntersecting) {
                // 옛 메시지 로드 전 현재 scrollHeight 저장 → 로드 후 scrollTop 보정
                prevScrollHeightRef.current = root.scrollHeight;
                await loadMore();
            }
        }, { root, threshold: 0.1 });

        io.observe(sentinel);
        return () => io.disconnect();
    }, [open, hasMore, loadMore]);

    // ─── 옛 메시지 prepend 후 스크롤 위치 보존 ──────────────
    useEffect(() => {
        const root = listRef.current;
        if (!root) return;
        const prev = prevScrollHeightRef.current;
        if (prev > 0) {
            const delta = root.scrollHeight - prev;
            if (delta > 0) root.scrollTop = delta;
            prevScrollHeightRef.current = 0;
        }
    }, [messages]);

    // ─── 새 메시지 도착 시 자동 스크롤 (가장 아래 보고 있을 때만) ──
    const scrollListener = useCallback(() => {
        const root = listRef.current;
        if (!root) return;
        const nearBottom = root.scrollHeight - root.scrollTop - root.clientHeight < 80;
        wasAtBottomRef.current = nearBottom;
    }, []);

    useEffect(() => {
        if (!open) return;
        const root = listRef.current;
        if (!root) return;
        root.addEventListener("scroll", scrollListener);
        return () => root.removeEventListener("scroll", scrollListener);
    }, [open, scrollListener]);

    useEffect(() => {
        if (!open) return;
        if (wasAtBottomRef.current) {
            bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [messages, streamingText, open]);

    // ─── 사용량 색상 — ds.colors.error/warning 토큰 사용 ────
    const usageColor = useMemo(() => {
        const ratio = usage.dailyLimit > 0 ? usage.todayUsed / usage.dailyLimit : 0;
        if (ratio >= 0.95) return ds.colors.error;
        if (ratio >= 0.80) return ds.colors.warning;
        return "rgba(255,255,255,0.85)";
    }, [usage]);

    // streaming 중인 ASSISTANT 메시지를 표시할 임시 객체
    const streamingBubble = sending && streamingText ? {
        id: "streaming",
        role: "ASSISTANT",
        content: streamingText,
        createdAt: new Date().toISOString(),
    } : null;

    const overLimit = usage.remainingToday != null && usage.remainingToday <= 0;
    if (!visible || !open) return null;

    return (
        <Paper elevation={8} sx={{
            position: "fixed",
            right: 24, bottom: 24,
            // 데스크톱 크기 — 기본을 작게(340×460) 두고, 축소 토글로 한 단계 더 줄인다(280×340).
            // 우하단 고정이라 창이 크면 표 오른쪽 열·하단 저장바를 가린다(모바일은 기존대로 전체 화면).
            width:  { xs: "calc(100vw - 32px)", sm: compact ? 280 : 340 },
            height: { xs: "calc(100vh - 32px)", sm: compact ? 340 : 460 },
            maxHeight: { sm: compact ? 340 : 460 },
            zIndex: 1300,
            display: "flex", flexDirection: "column",
            overflow: "hidden",
            borderRadius: 1.5,
        }}>
            {/* ── 헤더 ───────────────────────────────────────── */}
            <Stack direction="row" alignItems="center" spacing={compact ? 0.5 : 1}
                sx={{ px: 1.5, py: compact ? 0.8 : 1.2, bgcolor: ds.colors.primary, color: "#fff" }}>
                <SmartToyRoundedIcon sx={{ fontSize: compact ? 16 : 20 }} />
                <Typography sx={{ fontSize: compact ? 12.5 : 14, fontWeight: 700, flexShrink: 0 }}>
                    AI 비서
                </Typography>
                <Box sx={{ flex: 1 }} />
                {/* 축소 모드에선 토큰 사용량 텍스트를 숨겨 헤더 버튼이 밀리지 않게 한다. */}
                {!compact && (
                    <Tooltip title={`오늘 토큰 사용량 (한도 ${usage.dailyLimit.toLocaleString()})`}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: usageColor, mr: 0.5 }}>
                            {usage.todayUsed.toLocaleString()} / {usage.dailyLimit.toLocaleString()}
                        </Typography>
                    </Tooltip>
                )}
                <ChatbotMenu onReset={resetSession} onHide={hide} />
                <Tooltip title={compact ? "기본 크기로" : "더 작게"}>
                    <IconButton size="small" onClick={toggleCompact}>
                        {compact
                            ? <OpenInFullRoundedIcon  sx={{ fontSize: compact ? 15 : 18, color: "#fff" }} />
                            : <CloseFullscreenRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="최소화">
                    <IconButton size="small" onClick={toggleOpen}>
                        <MinimizeRoundedIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="숨기기">
                    <IconButton size="small" onClick={hide}>
                        <CloseRoundedIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* ── 메시지 영역 (무한 스크롤) ─────────────────────── */}
            <Box ref={listRef} sx={{
                flex: 1, overflowY: "auto",
                px: 1.5, py: 1.5,
                bgcolor: ds.colors.surface,
            }}>
                {/* top sentinel — 위로 스크롤 시 옛 메시지 로드 */}
                <Box ref={topSentinelRef} sx={{ height: 8 }} />
                {!hasMore && messages.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: ds.colors.textMuted, textAlign: "center", mb: 1 }}>
                        ↑ 처음입니다
                    </Typography>
                )}

                {messages.length === 0 && !sending && (
                    <Box sx={{ textAlign: "center", py: 4, color: ds.colors.textMuted }}>
                        <SmartToyRoundedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
                        <Typography sx={{ fontSize: 13 }}>
                            안녕하세요! 무엇을 도와드릴까요?
                        </Typography>
                    </Box>
                )}

                {messages.map((m) => (
                    <ChatbotMessageBubble key={`${m.id}-${m.role}`} message={m} />
                ))}

                {/* 응답 streaming 중 임시 bubble */}
                {streamingBubble && (
                    <ChatbotMessageBubble message={streamingBubble} />
                )}

                {/* "생각 중..." (응답 시작 전) */}
                {sending && !streamingText && (
                    <Box sx={{ px: 1.5, py: 1, color: ds.colors.textMuted, fontSize: 13 }}>
                        AI가 답변 중<span className="dots">...</span>
                    </Box>
                )}

                <Box ref={bottomAnchorRef} />
            </Box>

            {/* ── 입력창 ─────────────────────────────────────── */}
            <ChatbotInput
                disabled={sending || overLimit}
                placeholder={
                    overLimit
                        ? `오늘 토큰 한도(${usage.dailyLimit.toLocaleString()}) 도달 — 내일 다시 사용`
                        : undefined
                }
                onSend={sendMessage}
            />
        </Paper>
    );
}
